import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, RotateCcw } from 'lucide-react';

export default function RefundPolicy() {
  return (
    <div className="min-h-screen bg-[#050508] pt-24 pb-16">
      <div className="max-w-4xl mx-auto px-6">
        <Link to="/" className="inline-flex items-center gap-1.5 text-white/50 hover:text-white mb-6 transition-colors text-sm">
          <ArrowLeft size={14} /><span className="font-body">Back to Home</span>
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-[#10B981]/15 flex items-center justify-center">
              <RotateCcw className="w-6 h-6 text-[#10B981]" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-2xl md:text-3xl text-white">Refund & Cancellation Policy</h1>
              <p className="font-body text-white/40 text-sm">Last updated: August 2026</p>
            </div>
          </div>

          <div className="glass rounded-2xl p-6 md:p-8 space-y-6">
            <section>
              <h2 className="font-heading font-semibold text-lg text-white mb-3">1. Overview</h2>
              <p className="font-body text-white/60 text-sm leading-relaxed">
                This Refund and Cancellation Policy outlines the terms for subscription cancellations and refund requests for Monk Algo subscription services. We aim to be fair and transparent in our refund process.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-lg text-white mb-3">2. Subscription Cancellation</h2>
              <ul className="space-y-2">
                <li className="font-body text-white/60 text-sm flex items-start gap-2">
                  <span className="text-[#10B981] mt-1">•</span>
                  You may cancel your subscription at any time by contacting us at support@monkalgo.com
                </li>
                <li className="font-body text-white/60 text-sm flex items-start gap-2">
                  <span className="text-[#10B981] mt-1">•</span>
                  Cancellation takes effect at the end of your current billing period (quarterly or yearly)
                </li>
                <li className="font-body text-white/60 text-sm flex items-start gap-2">
                  <span className="text-[#10B981] mt-1">•</span>
                  You will continue to have access to our software until your subscription period ends
                </li>
                <li className="font-body text-white/60 text-sm flex items-start gap-2">
                  <span className="text-[#10B981] mt-1">•</span>
                  No partial refunds are provided for unused time within a billing period
                </li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-lg text-white mb-3">3. Refund Eligibility</h2>
              <div className="space-y-4">
                <div className="p-4 rounded-lg bg-[#10B981]/10 border border-[#10B981]/20">
                  <h3 className="font-heading font-medium text-[#10B981] text-sm mb-2">Eligible for Refund:</h3>
                  <ul className="space-y-1">
                    <li className="font-body text-white/60 text-sm flex items-start gap-2">
                      <span className="text-[#10B981] mt-1">•</span>
                      Technical issues preventing software installation or activation (TradingView access, MT5, or NT8 files) verified by our support team
                    </li>
                    <li className="font-body text-white/60 text-sm flex items-start gap-2">
                      <span className="text-[#10B981] mt-1">•</span>
                      Duplicate payment or billing errors
                    </li>
                    <li className="font-body text-white/60 text-sm flex items-start gap-2">
                      <span className="text-[#10B981] mt-1">•</span>
                      Service unavailability for extended periods (more than 7 consecutive days)
                    </li>
                  </ul>
                </div>

                <div className="p-4 rounded-lg bg-[#EF4444]/10 border border-[#EF4444]/20">
                  <h3 className="font-heading font-medium text-[#EF4444] text-sm mb-2">Not Eligible for Refund:</h3>
                  <ul className="space-y-1">
                    <li className="font-body text-white/60 text-sm flex items-start gap-2">
                      <span className="text-[#EF4444] mt-1">•</span>
                      Trading losses or unsatisfactory trading performance
                    </li>
                    <li className="font-body text-white/60 text-sm flex items-start gap-2">
                      <span className="text-[#EF4444] mt-1">•</span>
                      Change of mind after license activation
                    </li>
                    <li className="font-body text-white/60 text-sm flex items-start gap-2">
                      <span className="text-[#EF4444] mt-1">•</span>
                      Issues with your broker or trading platform not related to our software
                    </li>
                    <li className="font-body text-white/60 text-sm flex items-start gap-2">
                      <span className="text-[#EF4444] mt-1">•</span>
                      Violation of our Terms of Service
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-lg text-white mb-3">4. Refund Request Process</h2>
              <ol className="space-y-3">
                <li className="font-body text-white/60 text-sm">
                  <span className="text-[#007AFF] font-semibold">Step 1:</span> Email your refund request to support@monkalgo.com with your subscription details
                </li>
                <li className="font-body text-white/60 text-sm">
                  <span className="text-[#007AFF] font-semibold">Step 2:</span> Include the reason for your refund request and any relevant documentation
                </li>
                <li className="font-body text-white/60 text-sm">
                  <span className="text-[#007AFF] font-semibold">Step 3:</span> Our team will review your request within 3-5 business days
                </li>
                <li className="font-body text-white/60 text-sm">
                  <span className="text-[#007AFF] font-semibold">Step 4:</span> If approved, refunds will be processed within 7-10 business days
                </li>
              </ol>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-lg text-white mb-3">5. Refund Method</h2>
              <p className="font-body text-white/60 text-sm leading-relaxed">
                Approved refunds will be credited to the original payment method used during the subscription. The refund processing time may vary depending on your bank or payment provider (typically 5-10 business days after approval).
              </p>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-lg text-white mb-3">6. Refund Timeline</h2>
              <div className="grid sm:grid-cols-3 gap-3">
                <div className="p-4 rounded-lg bg-white/5 border border-white/10 text-center">
                  <div className="font-heading font-bold text-xl text-[#007AFF]">3-5</div>
                  <div className="font-body text-xs text-white/40">Days to review</div>
                </div>
                <div className="p-4 rounded-lg bg-white/5 border border-white/10 text-center">
                  <div className="font-heading font-bold text-xl text-[#8B5CF6]">7-10</div>
                  <div className="font-body text-xs text-white/40">Days to process</div>
                </div>
                <div className="p-4 rounded-lg bg-white/5 border border-white/10 text-center">
                  <div className="font-heading font-bold text-xl text-[#10B981]">5-10</div>
                  <div className="font-body text-xs text-white/40">Days to credit</div>
                </div>
              </div>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-lg text-white mb-3">7. Contact Us</h2>
              <p className="font-body text-white/60 text-sm leading-relaxed">
                For refund requests or questions about this policy, please contact us:
              </p>
              <div className="mt-3 p-4 rounded-lg bg-white/5 border border-white/10">
                <p className="font-body text-white/70 text-sm">Email: support@monkalgo.com</p>
                <p className="font-body text-white/50 text-xs mt-1">Response time: Within 24-48 hours</p>
              </div>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
